import { FC, ReactNode, useCallback, useLayoutEffect, useRef, useState, useEffect } from 'react';
import { createUseStyles } from 'react-jss';
import Chip from '../chip';
import { Box, Stack } from '@mui/material';

interface Props {
  children: ReactNode[];
  maxWidth?: number | string;
  gap?: number;
  className?: string;
}

const useStyles = createUseStyles({
  item: {
    display: 'inline-flex',
    minWidth: 0,
  },
  measure: {
    position: 'absolute',
    visibility: 'hidden',
    height: 0,
    overflow: 'hidden',
    pointerEvents: 'none',
    whiteSpace: 'nowrap',
  },
});

const MoreCountList: FC<Props> = ({ children, maxWidth = '100%', gap = 4, className }) => {
  const styles = useStyles();
  const containerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(children.length);
  const [overflowCount, setOverflowCount] = useState(0);

  const handleUpdate = useCallback(() => {
    const container = containerRef.current;
    const measure = measureRef.current;
    if (!container || !measure) {
      return;
    }

    const containerWidth = container.clientWidth;
    if (!containerWidth) {
      return;
    }

    const itemEls = Array.from(measure.querySelectorAll('[data-chip-item]'));
    const itemWidths = itemEls.map((el) => el.getBoundingClientRect().width);

    if (!itemWidths.length) {
      setVisibleCount(0);
      setOverflowCount(0);
      return;
    }

    const overflowEls = Array.from(measure.querySelectorAll('[data-overflow-count]'));
    const overflowWidths: Map<number, number> = new Map();
    overflowEls.forEach((el) => {
      const count = Number(el.getAttribute('data-overflow-count') || 0);
      if (count > 0) {
        overflowWidths.set(count, el.getBoundingClientRect().width);
      }
    });

    const totalItemCount = itemWidths.length;
    let nextVisible = totalItemCount;

    for (let count = totalItemCount; count >= 0; count -= 1) {
      const hidden = totalItemCount - count;
      const overflowWidth = hidden > 0 ? overflowWidths.get(hidden) || 0 : 0;
      const itemsWidth = count > 0 ? itemWidths.slice(0, count).reduce((sum, w) => sum + w, 0) : 0;
      const itemsGap = count > 1 ? gap * (count - 1) : 0;
      const overflowGap = hidden > 0 && count > 0 ? gap : 0;
      const totalWidth = itemsWidth + itemsGap + overflowWidth + overflowGap;

      if (totalWidth <= containerWidth) {
        nextVisible = count;
        break;
      }
    }

    setVisibleCount(nextVisible);
    setOverflowCount(totalItemCount - nextVisible);
  }, [gap]);

  useLayoutEffect(() => {
    handleUpdate();
  }, [children, maxWidth, gap, handleUpdate]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', handleUpdate);
      return () => window.removeEventListener('resize', handleUpdate);
    }

    const observer = new ResizeObserver(() => handleUpdate());
    observer.observe(container);
    return () => observer.disconnect();
  }, [handleUpdate]);

  return (
    <Stack
      direction="row"
      alignItems="center"
      flexWrap="nowrap"
      overflow="hidden"
      minWidth={0}
      width="100%"
      position="relative"
      className={className}
      style={{ maxWidth, gap }}
      ref={containerRef}
    >
      {children.slice(0, visibleCount).map((item, index) => (
        <span key={`chip-${index}`} className={styles.item}>
          {item}
        </span>
      ))}
      {overflowCount > 0 && <Chip label={`+${overflowCount}`} size="small" />}
      <Box className={styles.measure} ref={measureRef} aria-hidden="true">
        {children.map((item, index) => (
          <span key={`measure-${index}`} data-chip-item className={styles.item}>
            {item}
          </span>
        ))}
        {children.map((_, index) => (
          <span
            key={`measure-overflow-${index + 1}`}
            data-overflow-count={index + 1}
            className={styles.item}
          >
            <Chip label={`+${index + 1}`} size="small" />
          </span>
        ))}
      </Box>
    </Stack>
  );
};

export default MoreCountList;
